import { useMemo } from 'react'
import type { ActivityType, UserData } from '../../types'
import { uniqueRecentNames, activitySeries, periodWindowLabel, type Period } from '../../utils/analytics'
import { TYPE_META } from '../../utils/entries'
import { LineChart } from './ChartCanvas'

interface ActivityChartsProps {
  data: UserData
  type: ActivityType
  period: Period
}

export default function ActivityCharts({ data, type, period }: ActivityChartsProps) {
  const names = useMemo(() => uniqueRecentNames(data, type, period), [data, type, period])
  const yLabel = type === 'weights' ? 'Kg (× reps)' : 'Calories'

  if (names.length === 0) {
    return (
      <div className="card text-center text-[var(--text-secondary)] mt-4">
        No {TYPE_META[type].label.toLowerCase()} logged in {periodWindowLabel(period)}.
      </div>
    )
  }

  return (
    <div className="space-y-4 mt-4">
      {names.map((name) => (
        <ActivityChart key={name} data={data} type={type} name={name} yLabel={yLabel} period={period} />
      ))}
    </div>
  )
}

function ActivityChart({
  data,
  type,
  name,
  yLabel,
  period,
}: {
  data: UserData
  type: ActivityType
  name: string
  yLabel: string
  period: Period
}) {
  const series = useMemo(() => activitySeries(data, type, name, period), [data, type, name, period])
  return (
    <section className="card">
      <h3 className="font-semibold mb-3">{name}</h3>
      <LineChart labels={series.labels} data={series.data} yLabel={yLabel} />
    </section>
  )
}
